import axios, { AxiosError } from "axios";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import {
  Autocomplete,
  Button,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Order, stableSort, getComparator } from "../ui/Tables";
import { oraPrenotaStore } from "../oraPrenotaStore";
import { memo, useEffect, useState } from "react";
import { Loading } from "../ui/Loading";
import { EuroFormatter } from "../utils/EuroFormatter";
import { TransazioneApi } from "./Transazione";
import { format } from "date-fns";
import { createDateAsUTC, readDate } from "../utils/dateUtils";
import * as ExcelJS from "exceljs";
import { downloadExcel } from "../utils/excel";
import { CardsData } from "../prenotazione/CardsData";
import { DatePicker } from "@mui/x-date-pickers";
import { Transazioni_Causali } from "../prismaTypes";
import { RigaSocio } from "../prenotazione/RigaSocio";
import { queryClient } from "../App";

interface HeadCell {
  disablePadding: boolean;
  id: keyof TransazioneApi;
  label: string;
  numeric: boolean;
  key: string;
  filter: boolean;
  width: string;
}

const headCells: readonly HeadCell[] = [
  {
    id: "dataTransazione",
    numeric: false,
    disablePadding: false,
    label: "Data",
    key: "dataTransazione",
    filter: true,
    width: "100px",
  },
  {
    id: "importo",
    numeric: true,
    disablePadding: false,
    label: "Importo",
    key: "importo",
    filter: true,
    width: "80px",
  },
  {
    id: "causale",
    numeric: false,
    disablePadding: false,
    label: "Causale",
    key: "causale",
    filter: true,
    width: "200px",
  },
  {
    id: "codiceCard",
    numeric: false,
    disablePadding: false,
    label: "Codice Card",
    key: "codiceCard",
    filter: true,
    width: "90px",
  },
  {
    id: "cognome",
    numeric: false,
    disablePadding: false,
    label: "Nominativo",
    key: "cognome",
    filter: true,
    width: "100px",
  },
  {
    id: "saldoCorrente",
    numeric: false,
    disablePadding: false,
    label: "Saldo Conto",
    key: "saldoCorrente",
    filter: true,
    width: "80px",
  },
  {
    id: "risorsa",
    numeric: false,
    disablePadding: false,
    label: "Risorsa",
    key: "risorsa",
    filter: true,
    width: "120px",
  },
  {
    id: "note",
    numeric: false,
    disablePadding: false,
    label: "Note Movimento",
    key: "note",
    filter: true,
    width: "50px",
  },
  {
    id: "noteP",
    numeric: false,
    disablePadding: false,
    label: "Note Prenotazione",
    key: "noteP",
    filter: true,
    width: "50px",
  },
  {
    id: "id",
    numeric: true,
    disablePadding: false,
    label: "Id",
    key: "id",
    filter: true,
    width: "30px",
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof TransazioneApi) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  onFilter: (property: keyof TransazioneApi, filter: string | number) => void;
}

const EnhancedTableHead = memo(function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, onFilter } = props;
  const createSortHandler =
    (property: keyof TransazioneApi) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const debounceTimers = React.useRef<Record<string, number>>({});

  const createFilterHandler =
    (property: keyof TransazioneApi) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      // clear debounce precedente
      if (debounceTimers.current[property as string]) {
        clearTimeout(debounceTimers.current[property as string]);
      }

      // nuovo debounce
      debounceTimers.current[property as string] = window.setTimeout(() => {
        onFilter(property, value);
      }, 300);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.key ? headCell.key : headCell.id}>
            {headCell.filter && (
              <TextField
                label=""
                variant="standard"
                onChange={createFilterHandler(headCell.id)}
                sx={{ maxWidth: headCell.width }}
              />
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
});

function GetDataFine(dataFine: Date) {
  const data = new Date(dataFine);
  data.setDate(data.getDate() + 1);
  return data;
}

export function ListaTransazioni() {
  const [dataInizio, setInizio] = useState<Date | null>(null);
  const [dataFine, setFine] = useState<Date | null>(null);
  const [idConto, setIdConto] = useState<number | null>(null);
  const [idCausale, setIdCausale] = useState<string | null>(null);

  const { isFetching, data, refetch } = useQuery<{ transazioni: TransazioneApi[] }, AxiosError>(
    ["transazioni/find"],
    () =>
      axios
        .post(API_URL + "transazioni/find", {
          dataDa: dataInizio ? createDateAsUTC(dataInizio).toISOString().split("T")[0] : undefined,
          dataFine: dataFine
            ? createDateAsUTC(GetDataFine(dataFine)).toISOString().split("T")[0]
            : undefined,
          idConto: idConto && idConto > 0 ? idConto : undefined,
          idCausale:
            idCausale && idCausale.length > 1
              ? idCausale.startsWith("C")
                ? parseInt(idCausale.substring(1))
                : undefined
              : undefined,
          idCausaleSys:
            idCausale && idCausale.length > 1
              ? idCausale.startsWith("S")
                ? parseInt(idCausale.substring(1))
                : undefined
              : undefined,
        })
        .then((res) => res.data),
    { enabled: false, refetchOnWindowFocus: false, refetchOnMount: false, cacheTime: 0 },
  );

  useEffect(() => {
    queryClient.removeQueries(["transazioni/find"]);
  }, []);

  const filtra = () => {
    if (dataInizio !== null && dataFine !== null) refetch();
  };

  const rows = data?.transazioni ?? [];

  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<keyof TransazioneApi>("id");
  const [page, setPage] = React.useState(0);
  const dense = oraPrenotaStore((state) => state.tabelleCompatte);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof TransazioneApi) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [filters, setFilters] = useState({});
  const handleFilterRequest = (
    //  event: React.MouseEvent<unknown>,
    property: keyof TransazioneApi,
    filter: string | number,
  ) => {
    setFilters({
      ...filters,
      [property]: filter,
    });
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const transazioni = stableSort(rows, getComparator(order, orderBy), filters);

  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Transazioni");
    sheet.columns = [
      {
        header: "Data Transazione",
        key: "dataTransazione",
        width: 15,
        style: { numFmt: "dd/mm/yyyy" },
      },
      {
        header: "Importo",
        key: "importo",
        width: 15,
        style: { numFmt: "#,##0.00" },
      },
      { header: "Causale", key: "causale", width: 40 },
      { header: "Codice Card", key: "codiceCard", width: 20 },
      { header: "Nome", key: "nome", width: 30 },
      { header: "Cognome", key: "cognome", width: 30 },
      {
        header: "Saldo Corrente",
        key: "saldoCorrente",
        width: 15,
        style: { numFmt: "#,##0.00" },
      },
      { header: "Risorsa", key: "risorsa", width: 120 },
      { header: "Note Transazione", key: "note", width: 100 },
      { header: "Note Prenotazione", key: "noteP", width: 100 },
      { header: "Id", key: "id", width: 15 },
    ];

    transazioni.forEach((row) =>
      sheet.addRow({
        dataTransazione: readDate(row.dataTransazione),
        importo: row.importo,
        causale: row.causale,
        codiceCard: row.codiceCard,
        nome: row.nome,
        cognome: row.cognome,
        saldoCorrente: row.saldoCorrente,
        note: row.note,
        noteP: row.noteP,
        risorsa: row.risorsa,
        id: row.id,
      }),
    );

    const buff = await workbook.xlsx.writeBuffer();
    downloadExcel(buff, "transazioni" + ".xlsx");
  };

  const { isLoading, data: membri } = useQuery<{ cards: CardsData[] }, AxiosError>(
    ["cards/findAllPrenota"],
    () => axios.get(API_URL + "cards/findAllPrenota").then((res) => res.data),
    { refetchOnMount: false, refetchOnWindowFocus: false },
  );
  const { isLoading: isLoading2, data: causali } = useQuery<
    { causali: Transazioni_Causali[] },
    AxiosError
  >(["causali"], () => axios.get(API_URL + "causali").then((res) => res.data), {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const { isLoading: isLoading3, data: causaliSys } = useQuery<
    { causali: Transazioni_Causali[] },
    AxiosError
  >(["causali/sys"], () => axios.get(API_URL + "causali/sys").then((res) => res.data), {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  if (isLoading || isLoading2 || isLoading3) return <Loading />;

  const conti = membri?.cards ?? [];

  return (
    <Stack sx={{ width: "100%" }} direction={"column"} gap={2}>
      <Stack
        direction={["column", "row"]}
        gap={3}
        justifyContent={["center", "center", "flex-start"]}
        alignItems="center"
        sx={{ flexWrap: "wrap" }}
      >
        <DatePicker
          label="Dal"
          value={dataInizio}
          onChange={(newValue) => {
            setInizio(newValue);
          }}
          renderInput={(params) => (
            <TextField {...params} variant="standard" sx={{ width: 150 }} type="date" />
          )}
        />
        <DatePicker
          label="Al"
          value={dataFine}
          onChange={(newValue) => {
            setFine(newValue);
          }}
          renderInput={(params) => (
            <TextField {...params} variant="standard" sx={{ width: 150 }} type="date" />
          )}
        />

        <Autocomplete
          sx={{ width: 300 }}
          getOptionLabel={(option) => option.codice + " " + option.nome + " " + option.cognome}
          options={conti}
          onChange={(event: any, newValue: CardsData | null) => setIdConto(newValue?.idConto ?? -1)}
          value={conti.find((c) => c.idConto === idConto) || null}
          renderOption={(props, option) => (
            <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
              <RigaSocio option={option} />
            </Box>
          )}
          renderInput={(params) => <TextField {...params} label="Conto" variant="standard" />}
        />

        <div>
          <InputLabel>Causale</InputLabel>
          <Select
            sx={{ width: 250 }}
            label="Tipologia"
            variant="standard"
            value={idCausale ?? ""}
            onChange={(e) => setIdCausale(e.target.value)}
          >
            <MenuItem value={-1}>-Tutte-</MenuItem>
            {causali?.causali?.map((ca) => (
              <MenuItem key={ca.id} value={"C" + ca.id}>
                {ca.descrizione}
              </MenuItem>
            ))}
            {causaliSys?.causali.map((ca) => (
              <MenuItem key={ca.id} value={"S" + ca.id}>
                {ca.descrizione}
              </MenuItem>
            ))}
          </Select>
        </div>

        <Button type="submit" variant="contained" onClick={() => filtra()}>
          Filtra
        </Button>

        <Button variant="contained" color="success" onClick={() => exportExcel()}>
          Excel
        </Button>
      </Stack>

      <>
        <TableContainer>
          <Table aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              onFilter={handleFilterRequest}
            />
            <TableBody classes={{ root: "table-body" }}>
              {isFetching && <Loading />}

              {!isFetching &&
                transazioni
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: TransazioneApi) => {
                    return <TranTableRow key={row.id} row={row} />;
                  })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </>
    </Stack>
  );
}
const TranTableRow = memo(function TranTableRow(props: { row: TransazioneApi }) {
  const { row } = props;
  return (
    <TableRow hover tabIndex={-1} key={row.id}>
      <TableCell>{format(readDate(row.dataTransazione), "dd/MM/yyyy")}</TableCell>
      <TableCell align="right">{EuroFormatter.format(row.importo)}</TableCell>
      <TableCell>{row.causale}</TableCell>
      <TableCell>{row.codiceCard}</TableCell>
      <TableCell>
        {row.cognome} {row.nome}
      </TableCell>
      <TableCell align="right">
        {row.saldoCorrente !== null && EuroFormatter.format(row.saldoCorrente)}
      </TableCell>
      <TableCell>{row.risorsa}</TableCell>
      <TableCell>{row.note}</TableCell>
      <TableCell>{row.noteP}</TableCell>
      <TableCell align="right">{row.id}</TableCell>
    </TableRow>
  );
});
